
import moment from 'moment'
import numbro from 'numbro'

import { mapActions, mapGetters } from 'vuex'

import TextBoxMetric from '@/components/TextBoxMetric.vue'
import MarketingSpend from '@/components/simulationDetails/MarketingSpend.vue'
import MarketingSales from '@/components/simulationDetails/MarketingSales.vue'
import PercentChip from '@/components/PercentChip.vue'
import { prettyNames } from '@/mixins/prettyNames.js'
import { isSpendUpTo, investedBudget } from '@/utils/optimizerBudget.js'
import { hasHiddenKpis, isKpiHidden, visibleOutputNames, firstVisibleOutputName } from '@/utils/hiddenKpis.js'

export default {
  components: {
    TextBoxMetric,
    MarketingSpend,
    MarketingSales,
    PercentChip
  },
  props: {
    simulation: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    colors: {
      type: Object,
      required: true
    },
    tableMode: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    customKpiName: {
      type: String,
      required: true
    }
  },
  methods: {
    ...mapActions('simulations', [
      'deleteSimulation',
      'updateSimulationSettings'
    ]),
    channelName (channel) {
      if (this.client && this.client.channels && channel in this.client.channels) {
        return this.client.channels[channel].name
      } else {
        return channel
      }
    },
    timeRestrictionChannelLabel (channelId) {
      // channelId is an array of channel ids; legacy plans persisted a scalar.
      const ids = Array.isArray(channelId) ? channelId : [channelId]
      return ids.map(id => {
        if (id === '__all__') {
          return this.$t('components.Simulation.allChannels')
        }
        const name = this.channelName(id)
        return this.prettyMediaNamesDict[name] ? this.prettyMediaNamesDict[name] : name
      }).join(' + ')
    },
    formatDate (date) {
      return moment(date).format('ll')
    },
    deleteThisSimulation () {
      this.deleteSimulation({
        client: this.client.id,
        simulation: this.simulation.id
      })
      this.deleteSimulationDialog = false
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    restrictionTypeName: function (type) {
      const types = {
        unrestricted: this.$t('components.Simulation.unrestricted'),
        more_than: this.$t('components.Simulation.moreThan'),
        less_than: this.$t('components.Simulation.lessThan'),
        between: this.$t('components.Simulation.between'),
        exactly: this.$t('components.Simulation.exactly')
      }
      return types[type]
    },
    openSettings () {
      this.settingsShowToExternalUsers = this.simulation.showToExternalUsers || false
      this.settingsDialog = true
    },
    saveSettings () {
      this.updateSimulationSettings({
        client: this.client.id,
        simulation: this.simulation.id,
        settings: { showToExternalUsers: this.settingsShowToExternalUsers }
      })
      this.settingsDialog = false
    },
    viewJSON: function () {
      console.log(JSON.stringify(this.simulation))
    },
    aggregateSalesDecompositionByPeriod (byPeriod, mode = 'sum') {
      if (!byPeriod || typeof byPeriod !== 'object') {
        return { components: [], total: 0 }
      }
      const components = Object.keys(byPeriod).map(name => {
        const ys = (byPeriod[name] && Array.isArray(byPeriod[name].y)) ? byPeriod[name].y : []
        const sum = ys.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
        const value = mode === 'average' && ys.length > 0 ? sum / ys.length : sum
        return { name, value }
      })
      const total = components.reduce((a, c) => a + c.value, 0)
      return { components, total }
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    referenceModelConfig () {
      if (this.client.plan_reference_model) {
        return this.client.plan_reference_model.config
      } else {
        return this.client.reference_model.config
      }
    },
    prettyMediaNamesDict () {
      return this.client.pretty_names ? this.client.pretty_names.media : {}
    },
    prettyColors () {
      return this.prettyTranslateNames(this.colors, [''], this.prettyMediaNamesDict)
    },
    channels () {
      if ('channels' in this.client) {
        return Object.keys(this.client.channels)
      } else {
        return []
      }
    },
    combinableOutputs () {
      const metadata = this.referenceModelConfig && this.referenceModelConfig.metadata
      const flagCombinable = !metadata || metadata.combinable_outputs !== false
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      const sales = this.simulation && this.simulation.results && this.simulation.results.sales
      const realOutputs = (sales && sales.outputs) ? Object.keys(sales.outputs) : []
      const allAdditive = realOutputs.every(name => {
        if (!configOutputs || !configOutputs[name]) return true
        return configOutputs[name].is_additive !== false
      })
      // The preview shows the Total, which adds up the hidden KPIs too: while any KPI is
      // hidden it is treated like a non-combinable model and shows no preview.
      const noHiddenKpis = realOutputs.length === 0 || !hasHiddenKpis(this.client)
      return flagCombinable && allAdditive && noHiddenKpis
    },
    // The only KPI left to show when the run has several outputs but just one is visible (the
    // others hidden by the model's Display settings) or it has a single output; null otherwise.
    // The preview then shows that KPI instead of the combined Total.
    singleVisibleOutputName () {
      const sales = this.simulation && this.simulation.results && this.simulation.results.sales
      if (!sales || !sales.outputs) return null
      const visible = visibleOutputNames(this.client, Object.keys(sales.outputs))
      return visible.length === 1 ? visible[0] : null
    },
    showPreview () {
      return this.combinableOutputs || !!this.singleVisibleOutputName
    },
    // The plan's manual KPI weights without the hidden KPIs.
    visibleKpiWeights () {
      const weights = (this.simulation && this.simulation.kpi_weighting && this.simulation.kpi_weighting.weights) || {}
      const visible = {}
      Object.keys(weights).forEach(key => {
        if (!isKpiHidden(this.client, key)) {
          visible[key] = weights[key]
        }
      })
      return visible
    },
    canViewMore () {
      return !!(this.simulation && this.simulation.results && this.currentOutputObject && this.currentOutputObject.total)
    },
    noPreviewReason () {
      // Outputs the run actually produced; a legacy single-output run has no
      // `outputs` key at all, so fall back to a message that names nothing.
      const sales = this.simulation && this.simulation.results && this.simulation.results.sales
      const outputNames = (sales && sales.outputs) ? visibleOutputNames(this.client, Object.keys(sales.outputs)) : []
      if (outputNames.length > 0) {
        return this.$t('components.Simulation.noPreviewReasonOutputs', { outputs: outputNames.join(', ') })
      }
      return this.$t('components.Simulation.noPreviewReasonGeneric')
    },
    currentOutputObject: function () {
      const sales = this.simulation.results.sales
      if (this.singleVisibleOutputName) {
        return sales.outputs[this.singleVisibleOutputName]
      } else if ('total' in sales) {
        return sales
      } else {
        const firstKey = firstVisibleOutputName(this.client, sales.outputs)
        return firstKey ? sales.outputs[firstKey] : null
      }
    },
    currentOutputUnitName: function () {
      const sales = this.simulation.results.sales
      if ('total' in sales && !this.singleVisibleOutputName) {
        return this.referenceModelConfig.sales_unit_name || 'USD'
      } else if ('outputs' in sales) {
        const firstKey = this.singleVisibleOutputName || firstVisibleOutputName(this.client, sales.outputs)
        const configOutput = firstKey && this.referenceModelConfig.outputs && this.referenceModelConfig.outputs[firstKey]
        return (configOutput && configOutput.sales_unit_name) || 'USD'
      } else {
        return 'USD'
      }
    },
    currentOutputUnitSymbol: function () {
      if (this.currentOutputUnitName === 'USD') {
        return '$'
      } else {
        return ''
      }
    },
    spendUnitName: function () {
      if ('invest_unit_name' in this.referenceModelConfig) {
        return this.referenceModelConfig.invest_unit_name
      } else {
        return 'USD'
      }
    },
    budgetDisplay: function () {
      // For spend_up_to beta plans, simulation.budget is just a ceiling; show the
      // real invested amount as the main figure and the ceiling as a caption.
      const invested = investedBudget(this.simulation)
      if (isSpendUpTo(this.simulation) && invested !== null) {
        return {
          value: invested,
          caption: this.$t('components.Simulation.upTo', { value: this.formatValue(this.simulation.budget) })
        }
      }
      return { value: this.simulation.budget, caption: '' }
    },
    salesDecompositionAggregationMode () {
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      const isOutputAdditive = (outputName) => {
        if (!configOutputs || !configOutputs[outputName]) return true
        return configOutputs[outputName].is_additive !== false
      }
      const sales = this.simulation.results.sales
      if (this.singleVisibleOutputName) {
        return isOutputAdditive(this.singleVisibleOutputName) ? 'sum' : 'average'
      } else if ('total' in sales) {
        const realOutputs = sales.outputs ? Object.keys(sales.outputs) : []
        const allAdditive = realOutputs.every(name => isOutputAdditive(name))
        return allAdditive ? 'sum' : 'average'
      } else {
        const firstName = firstVisibleOutputName(this.client, sales.outputs)
        return isOutputAdditive(firstName) ? 'sum' : 'average'
      }
    },
    mediaAttributedDecompositionByPeriodData () {
      const obj = this.currentOutputObject
      if (!obj || !obj.media_attributed_sales_decomposition_by_period) {
        return null
      }
      const aggregated = this.aggregateSalesDecompositionByPeriod(
        obj.media_attributed_sales_decomposition_by_period,
        this.salesDecompositionAggregationMode
      )
      const marketing = {}
      aggregated.components.forEach(c => {
        const prettyName = this.prettyMediaNamesDict[c.name] || c.name
        marketing[prettyName] = { adstock: c.value }
      })
      return { marketing }
    }
  },
  data () {
    return {
      deleteSimulationDialog: false,
      settingsDialog: false,
      settingsShowToExternalUsers: false
    }
  },
  mixins: [prettyNames]
}
